// src/pages/admin/EventsManagement.js
import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    Grid,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Menu,
    MenuItem,
} from "@mui/material";
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as ViewIcon,
    Group as GroupIcon,
    EventAvailable as EventIcon,
    CheckCircle as CheckIcon,
    MoreVert as MoreIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { getAllEvents, deleteEvent, setEventStatus } from "../../services/eventService";
import AdminLayout from "../AdminLayout";

export default function EventsManagement() {
    const navigate = useNavigate();
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuEvent, setMenuEvent] = useState(null);

    useEffect(() => {
        fetchEvents();
    }, []);

    const fetchEvents = async () => {
        try {
            setLoading(true);
            const data = await getAllEvents();
            setEvents(data);
        } catch {
            setError("Failed to fetch events");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        try {
            await deleteEvent(selectedEvent.id);
            setSuccess("Event deleted successfully");
            setDeleteDialogOpen(false);
            fetchEvents();
        } catch {
            setError("Failed to delete event");
        }
    };

    const openMenu = (event, ev) => {
        setMenuEvent(ev);
        setAnchorEl(event.currentTarget);
    };

    const closeMenu = () => {
        setAnchorEl(null);
        setMenuEvent(null);
    };

    const handleStatusChange = async (status) => {
        if (!menuEvent) return;
        try {
            await setEventStatus(menuEvent.id, status);
            setSuccess(`Event status updated to ${status}`);
            closeMenu();
            fetchEvents();
        } catch {
            setError("Failed to update event status");
        }
    };

    const getStatusChip = (status) => {
        const map = {
            Upcoming: { color: "info", label: "Upcoming" },
            Ongoing: { color: "success", label: "Ongoing" },
            Completed: { color: "primary", label: "Completed" },
            Closed: { color: "default", label: "Closed" },
        };
        const conf = map[status] || map.Upcoming;
        return <Chip label={conf.label} color={conf.color} size="small" />;
    };

    return (
        <AdminLayout title="Events Management">
            <Box>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 3,
                    }}
                >
                    <Typography variant="h5" sx={{ fontWeight: 600, color: "#003366" }}>
                        Events Management
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => navigate("/admin/events/create")}
                        sx={{
                            backgroundColor: "#C1272D",
                            "&:hover": { backgroundColor: "#a01f25" },
                        }}
                    >
                        Create Event
                    </Button>
                </Box>

                {error && (
                    <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                {success && (
                    <Alert severity="success" onClose={() => setSuccess("")} sx={{ mb: 2 }}>
                        {success}
                    </Alert>
                )}

                {/* Stats */}
                <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={4}>
                        <Card
                            sx={{
                                background: "linear-gradient(135deg, #003366 0%, #004d99 100%)",
                                color: "#fff",
                            }}
                        >
                            <CardContent>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <EventIcon sx={{ fontSize: 40 }} />
                                    <Box>
                                        <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                            {events.length}
                                        </Typography>
                                        <Typography variant="body2">Total Events</Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Card
                            sx={{
                                background: "linear-gradient(135deg, #C1272D 0%, #e63946 100%)",
                                color: "#fff",
                            }}
                        >
                            <CardContent>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <GroupIcon sx={{ fontSize: 40 }} />
                                    <Box>
                                        <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                            {events.reduce(
                                                (sum, e) => sum + (e.registrations?.length || 0),
                                                0
                                            )}
                                        </Typography>
                                        <Typography variant="body2">Total Registrations</Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Card
                            sx={{
                                background: "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                                color: "#fff",
                            }}
                        >
                            <CardContent>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <CheckIcon sx={{ fontSize: 40 }} />
                                    <Box>
                                        <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                            {events.filter((e) => e.certificatesUploaded).length}
                                        </Typography>
                                        <Typography variant="body2">Certificates Issued</Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Event List */}
                <Grid container spacing={3}>
                    {loading ? (
                        <Grid item xs={12}>
                            <Typography align="center">Loading events...</Typography>
                        </Grid>
                    ) : events.length === 0 ? (
                        <Grid item xs={12}>
                            <Card>
                                <CardContent>
                                    <Typography align="center" color="textSecondary">
                                        No events found. Create your first event!
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ) : (
                        events.map((ev) => (
                            <Grid item xs={12} md={6} lg={4} key={ev.id}>
                                <Card
                                    sx={{
                                        height: "100%",
                                        display: "flex",
                                        flexDirection: "column",
                                        border: "1px solid #e0e0e0",
                                        "&:hover": {
                                            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                                            transform: "translateY(-4px)",
                                            transition: "all 0.3s ease",
                                        },
                                    }}
                                >
                                    <CardContent sx={{ flexGrow: 1 }}>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "flex-start",
                                                mb: 2,
                                            }}
                                        >
                                            <Typography
                                                variant="h6"
                                                sx={{ fontWeight: 600, color: "#003366", mb: 1 }}
                                            >
                                                {ev.title}
                                            </Typography>
                                            {getStatusChip(ev.eventStatus)}
                                        </Box>

                                        <Typography
                                            variant="body2"
                                            color="textSecondary"
                                            sx={{ mb: 2, minHeight: 40 }}
                                        >
                                            {ev.description}
                                        </Typography>

                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="caption" color="textSecondary">
                                                📅 Event Date:{" "}
                                                {ev.eventDate
                                                    ? new Date(ev.eventDate).toLocaleDateString()
                                                    : "Not set"}
                                            </Typography>
                                            <br />
                                            <Typography variant="caption" color="textSecondary">
                                                📍 Venue: {ev.venue || "TBA"}
                                            </Typography>
                                            <br />
                                            <Typography variant="caption" color="textSecondary">
                                                👥 Registrations: {ev.registrations?.length || 0}
                                                {ev.maxCapacity && ` / ${ev.maxCapacity}`}
                                            </Typography>
                                        </Box>

                                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                            {ev.attendanceMarked && (
                                                <Chip
                                                    label="Attendance Marked"
                                                    size="small"
                                                    color="success"
                                                    variant="outlined"
                                                />
                                            )}
                                            {ev.certificatesUploaded && (
                                                <Chip
                                                    label="Certificates Issued"
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                />
                                            )}
                                        </Box>
                                    </CardContent>

                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            p: 1,
                                            borderTop: "1px solid #e0e0e0",
                                        }}
                                    >
                                        <Box>
                                            <IconButton
                                                size="small"
                                                onClick={() => navigate(`/admin/events/${ev.id}`)}
                                                sx={{ color: "#003366" }}
                                            >
                                                <ViewIcon />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => navigate(`/admin/events/${ev.id}/edit`)}
                                                sx={{ color: "#C1272D" }}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    setSelectedEvent(ev);
                                                    setDeleteDialogOpen(true);
                                                }}
                                                sx={{ color: "#dc3545" }}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Box>
                                        <IconButton
                                            size="small"
                                            onClick={(e) => openMenu(e, ev)}
                                            sx={{ color: "#003366" }}
                                        >
                                            <MoreIcon />
                                        </IconButton>
                                    </Box>
                                </Card>
                            </Grid>
                        ))
                    )}
                </Grid>

                {/* Status Menu */}
                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
                    <MenuItem onClick={() => handleStatusChange("Upcoming")}>
                        Set as Upcoming
                    </MenuItem>
                    <MenuItem onClick={() => handleStatusChange("Ongoing")}>
                        Set as Ongoing
                    </MenuItem>
                    <MenuItem onClick={() => handleStatusChange("Completed")}>
                        Set as Completed
                    </MenuItem>
                    <MenuItem onClick={() => handleStatusChange("Closed")}>
                        Close Event (no registrations)
                    </MenuItem>
                </Menu>

                {/* Delete Dialog */}
                <Dialog
                    open={deleteDialogOpen}
                    onClose={() => setDeleteDialogOpen(false)}
                >
                    <DialogTitle>Confirm Delete</DialogTitle>
                    <DialogContent>
                        <Typography>
                            Are you sure you want to delete "{selectedEvent?.title}"?
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                        <Button onClick={handleDelete} color="error" variant="contained">
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </AdminLayout>
    );
}
